// Hook reutilizável de confirmação baseado no AlertDialog do shadcn.
// Substitui window.confirm por um diálogo consistente (e com data-testid).
//
// Uso:
//   const [confirm, confirmDialog] = useConfirm();
//   ...
//   const ok = await confirm({ title: "Excluir item?", description: "..." });
//   if (ok) { ...deleta... }
//   ...
//   return (<div>{confirmDialog} ...resto da página...</div>);
import React, { useState, useRef, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

export function useConfirm() {
  const [state, setState] = useState({ open: false, options: {} });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  const settle = (result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState((s) => ({ ...s, open: false }));
  };

  const { open, options } = state;
  const destructive = options.destructive !== false; // padrão: ação destrutiva (vermelho)

  const dialog = (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) settle(false); }}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="confirm-dialog-title">
            {options.title || "Confirmar ação"}
          </AlertDialogTitle>
          {options.description && (
            <AlertDialogDescription data-testid="confirm-dialog-description">
              {options.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn" onClick={() => settle(false)}>
            {options.cancelText || "Cancelar"}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-ok-btn"
            onClick={() => settle(true)}
            className={destructive ? "bg-red-600 text-white hover:bg-red-700 focus:ring-red-600" : ""}
          >
            {options.confirmText || "Confirmar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return [confirm, dialog];
}
